import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowLeft, ChevronLeft, Layers, Save, Send } from "lucide-react";
import { toast } from "react-toastify";

import apiClient from "../../../api-client.js";
import {
  documentUploadErrorText,
  publishingErrorText,
  updateAdaptationCallToAction,
  updateAdaptationFormat,
  uploadAdaptationDocument,
  uploadAdaptationVideo,
  videoUploadErrorText,
} from "../../../services/socialPublishingService.js";
import { Button } from "../../../components/Button";
import { ConfirmModal } from "../../../components/ConfirmModal";
import { useAuth } from "../../../components/contexts/AuthContext";
import { getData } from "../../../components/hooks/getData";
import { panelSx } from "../campaign/campaignUi";
import {
  AddNetworkButton,
  ManualNetworkCard,
  hasText,
  replaceLocalImage,
  revokeLocalImages,
} from "../workflow/ManualNetworkCard";
import { PostPhonePreview, hasPostPreview } from "../workflow/PostPreviewModal";
import { NO_CALL_TO_ACTION } from "../workflow/callToAction";
import { NetworkTabsPanel, networkTabMeta } from "../workflow/ReviewView";
import { WorkflowStepper } from "../workflow/WorkflowStepper";
import {
  formatLabel,
  getFormatRule,
  getPlatformMeta,
  isDocumentMedia,
  isImageMedia,
  isVideoMedia,
  platformLabel,
} from "../workflow/platformMeta";
import { toLocalDocument, withDocumentTitle } from "../workflow/DocumentMedia";
import { handOffVideos } from "../workflow/videoHandoff";
import { getPieceWorkflow } from "../workflow/workflowUtils";
import { INTERNAL_CONTENT_PATH } from "./ContentPiecePage";
import { ReadOnlyClientNotice } from "../ReadOnlyClientNotice";
import { isClientReadOnly } from "../../../utils/clientAccess";
import { uploadInlineImage } from "../../../services/contentGenerationService";

/**
 * Manual piece — /HerramientasIA/contenidos/manual?config=<configId>&titulo=<title>.
 * Step 1 with the same box as the piece: one tab per network with its hand-written
 * (or pasted Markdown) text and images, and "+" to add another. Nothing is saved until "Guardar borrador",
 * or "Enviar al cliente", which also sends it to review.
 */
export function ManualPiecePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const configId = searchParams.get("config");
  const title = (searchParams.get("titulo") || "").trim();
  const { interceptorReady, user } = useAuth();
  const { setPageContext } = useOutletContext();
  const theme = useTheme();

  const [platforms, setPlatforms] = useState([]);
  const [texts, setTexts] = useState({});
  // Images per network, kept locally ({ id, image: previewUrl, file }) and uploaded on create.
  const [images, setImages] = useState({});
  // Publication format per network (Instagram, Facebook, TikTok); `post` when missing.
  const [formats, setFormats] = useState({});
  // Videos uploading after the piece is created: { index, total, name, progress, phase }.
  const [videoUpload, setVideoUpload] = useState(null);
  const [activePlatform, setActivePlatform] = useState(null);
  const [creatingAction, setCreatingAction] = useState(null); // "draft" | "send" while saving
  const isCreating = Boolean(creatingAction);
  const [platformToRemove, setPlatformToRemove] = useState(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;
  // Read when a video creates the piece: the card may change the format and upload in the same tick.
  const latestRef = useRef({});
  const formatsRef = useRef(formats);
  // Google Business post button per network ({ type, url }), applied once the piece exists.
  const [callToActions, setCallToActions] = useState({});
  latestRef.current = { ...latestRef.current, platforms, texts, images, title, callToActions };

  // Free the local previews when leaving the page.
  useEffect(() => () => Object.values(imagesRef.current).forEach(revokeLocalImages), []);

  const { data: config, isLoading, isError } = getData(
    configId ? `content-generation/configs/${configId}/` : null,
    interceptorReady && Boolean(configId),
    configId
  );

  const goToList = useCallback(() => navigate(INTERNAL_CONTENT_PATH), [navigate]);

  useEffect(() => {
    const pageLabel = title || "Nuevo contenido manual";
    setPageContext({
      title: (
        <>
          Contenidos:{"  "}
          <span style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}>{pageLabel}</span>
        </>
      ),
      showTitle: true,
      leftContent: (
        <IconButton
          onClick={goToList}
          aria-label="Volver a contenidos"
          size="small"
          sx={{ color: "primary.main", padding: "4px", "&:hover": { backgroundColor: "action.hover" } }}
        >
          <ChevronLeft size={20} />
        </IconButton>
      ),
      titleRightContent: null,
      rightContent: null,
      breadcrumbItems: [
        { label: "Contenidos", onClick: goToList },
        { label: `${pageLabel}${config?.client_name ? ` · ${config.client_name}` : ""}` },
      ],
    });
  }, [setPageContext, goToList, title, config?.client_name, theme.palette.text.secondary]);

  // Stories publish no text: they need their photo or video instead.
  const needsText = (platform) => !getFormatRule(platform, formats[platform] || "post").noText;
  const missingText = platforms.filter((platform) => needsText(platform) && !hasText(texts[platform]));
  const missingMedia = platforms.filter((platform) => !needsText(platform) && !(images[platform] || []).length);
  // A draft saves half-written; sending needs every network complete.
  // The buttons keep their look while saving (no flicker); handleCreate ignores a second click.
  const canSaveDraft = platforms.length > 0;
  const canSend = canSaveDraft && missingText.length === 0 && missingMedia.length === 0;

  const addPlatform = (platform) => {
    setPlatforms((prev) => [...prev, platform]);
    setActivePlatform(platform);
  };

  const removePlatform = (platform) => {
    const remaining = platforms.filter((item) => item !== platform);
    setPlatforms(remaining);
    const withoutPlatform = (prev) => {
      const next = { ...prev };
      delete next[platform];
      return next;
    };
    setTexts(withoutPlatform);
    formatsRef.current = withoutPlatform(formatsRef.current);
    setFormats(formatsRef.current);
    setCallToActions(withoutPlatform);
    revokeLocalImages(images[platform]);
    setImages(withoutPlatform);
    if (activePlatform === platform) setActivePlatform(remaining[remaining.length - 1] || null);
  };

  // Only ask when there is something to lose.
  const requestRemove = (platform) => {
    const hasContent = hasText(texts[platform]) || (images[platform] || []).length > 0;
    if (hasContent) setPlatformToRemove(platform);
    else removePlatform(platform);
  };

  const addImages = (platform, localImages) =>
    setImages((prev) => ({ ...prev, [platform]: [...(prev[platform] || []), ...localImages] }));

  const cropImage = (platform, image, cropped) =>
    setImages((prev) => ({ ...prev, [platform]: replaceLocalImage(prev[platform], image, cropped) }));

  const addDocument = (platform, file, title) =>
    setImages((prev) => ({ ...prev, [platform]: [...(prev[platform] || []), toLocalDocument(file, title)] }));

  const changeDocumentTitle = (platform, title) =>
    setImages((prev) => ({ ...prev, [platform]: withDocumentTitle(prev[platform], title) }));

  const changeFormat = (platform, format) => {
    formatsRef.current = { ...formatsRef.current, [platform]: format };
    setFormats(formatsRef.current);
  };

  // POST create_manual with what is on screen; the videos go afterwards (it doesn't take them).
  const createDraftPiece = async ({ platforms: list, texts: currentTexts, images: currentImages }) => {
    const formData = new FormData();
    formData.append("config", configId);
    if (title) formData.append("title", title);
    formData.append(
      "adaptations",
      JSON.stringify(list.map((platform) => ({ platform, text: (currentTexts[platform] || "").trim() })))
    );
    list.forEach((platform) =>
      (currentImages[platform] || [])
        .filter(isImageMedia)
        .forEach((image) => formData.append(`images_${platform}`, image.file))
    );
    const { data } = await apiClient.post("content-generation/pieces/create_manual/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  // A video saves the draft right away and opens it: the piece's card uploads the video with its progress.
  const addVideo = async (platform, file) => {
    if (isCreating) throw { response: { data: { error: "Se está guardando el contenido. Espera un momento." } } };
    setCreatingAction("draft");
    try {
      const piece = await createDraftPiece(latestRef.current);
      const failures = await applyFormatsAndVideos(piece);
      if (failures.length) toast.error(failures.join(" · "), { autoClose: 6000 });
      const adaptation = (piece.platform_adaptations || []).find((item) => item.platform === platform);
      if (adaptation) handOffVideos(piece.id, adaptation.id, [file]);
      toast.success("Borrador guardado. Subiendo el vídeo…", { autoClose: 1500 });
      navigate(`${INTERNAL_CONTENT_PATH}/pieza/${piece.id}${adaptation ? `?adaptation=${adaptation.id}` : ""}`, {
        replace: true,
      });
      return null;
    } catch (error) {
      setCreatingAction(null);
      throw error;
    }
  };

  // The videos take a while to upload after saving: closing the tab would lose them.
  useEffect(() => {
    if (!videoUpload) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [videoUpload]);

  /**
   * After `create_manual` (which takes neither formats, buttons nor videos): set each network's format
   * and Google Business button, then upload its videos one by one. Returns the failures as text.
   */
  const applyFormatsAndVideos = async (piece) => {
    const failures = [];
    const adaptationOf = (platform) =>
      (piece?.platform_adaptations || []).find((adaptation) => adaptation.platform === platform);

    for (const platform of latestRef.current.platforms) {
      const format = formatsRef.current[platform];
      const adaptation = adaptationOf(platform);
      if (!adaptation || !format || format === "post") continue;
      try {
        await updateAdaptationFormat(adaptation.id, format);
      } catch (error) {
        failures.push(`${platformLabel(platform)}: ${publishingErrorText(error, "no se ha podido guardar el formato")}`);
      }
    }

    for (const platform of latestRef.current.platforms) {
      const adaptation = adaptationOf(platform);
      if (!adaptation) continue;
      const callToAction = latestRef.current.callToActions[platform];
      if (callToAction?.type) {
        try {
          await updateAdaptationCallToAction(adaptation.id, callToAction);
        } catch (error) {
          failures.push(
            `${platformLabel(platform)}: ${publishingErrorText(error, "no se ha podido guardar el botón de la publicación")}`
          );
        }
      }
      const localDocument = (latestRef.current.images[platform] || []).find(isDocumentMedia);
      if (localDocument) {
        try {
          await uploadAdaptationDocument(adaptation.id, localDocument.file, localDocument.title);
        } catch (error) {
          failures.push(`«${localDocument.file.name}» (${platformLabel(platform)}): ${documentUploadErrorText(error)}`);
        }
      }
    }

    const queue = latestRef.current.platforms.flatMap((platform) =>
      (latestRef.current.images[platform] || []).filter(isVideoMedia).map((video) => ({ platform, video }))
    );
    for (const [index, { platform, video }] of queue.entries()) {
      const adaptation = adaptationOf(platform);
      if (!adaptation) continue;
      const status = { index: index + 1, total: queue.length, name: video.file.name, progress: 0, phase: "uploading" };
      setVideoUpload(status);
      try {
        await uploadAdaptationVideo(adaptation.id, video.file, {
          onProgress: (fraction) =>
            setVideoUpload({
              ...status,
              progress: Math.round(fraction * 100),
              phase: fraction >= 1 ? "processing" : "uploading",
            }),
        });
      } catch (error) {
        failures.push(`«${video.file.name}» (${platformLabel(platform)}): ${videoUploadErrorText(error)}`);
      }
    }
    setVideoUpload(null);
    return failures;
  };

  const removeImage = (platform, image) => {
    revokeLocalImages([image]);
    setImages((prev) => ({ ...prev, [platform]: (prev[platform] || []).filter((item) => item.id !== image.id) }));
  };

  // Saves the piece as a draft and, with `send`, sends it to the client for review.
  const handleCreate = async ({ send = false } = {}) => {
    if (isCreating || (send ? !canSend : !canSaveDraft)) return;
    setCreatingAction(send ? "send" : "draft");
    let pieceId = null;
    try {
      const data = await createDraftPiece({ platforms, texts, images });
      pieceId = data.id;
      const failures = await applyFormatsAndVideos(data);
      if (failures.length) {
        // Saved, but incomplete: it isn't sent to the client until the videos are in.
        toast.error(
          `Borrador guardado, pero falta algo por subir. ${failures.join(" · ")}. Puedes volver a intentarlo desde la pieza.`,
          { autoClose: 8000 }
        );
        navigate(`${INTERNAL_CONTENT_PATH}/pieza/${pieceId}?paso=revision`, { replace: true });
        return;
      }
      if (send) await apiClient.post(`content-generation/pieces/${pieceId}/send_to_review/`);
      // The toast container is global: it stays on screen after navigating to the piece.
      toast.success(send ? "Contenido enviado al cliente" : "Borrador guardado", { autoClose: 1500 });
      navigate(`${INTERNAL_CONTENT_PATH}/pieza/${pieceId}?paso=revision`, { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.error;
      // Already saved as a draft: it can be sent from its own page.
      if (pieceId) {
        toast.error(`Borrador guardado, pero no se ha podido enviar al cliente${detail ? `: ${detail}` : "."}`, {
          autoClose: 4000,
        });
        navigate(`${INTERNAL_CONTENT_PATH}/pieza/${pieceId}?paso=revision`, { replace: true });
        return;
      }
      console.error("Error creating manual piece:", error);
      toast.error(detail || "Error al guardar el contenido.", { autoClose: 3000 });
      setCreatingAction(null);
    }
  };

  if (!configId || isError) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ ...panelSx, p: 4, maxWidth: 520, mx: "auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1.5 }}>
          <Layers size={32} color="#9CA3AF" />
          <Typography sx={{ fontWeight: 800, fontSize: "1.0625rem", color: "#0F172A" }}>
            {configId ? "No se ha podido cargar el cliente" : "Elige un cliente para crear contenido"}
          </Typography>
          <Button variant="outline" onClick={goToList}>
            <ArrowLeft size={15} /> Volver a contenidos
          </Button>
        </Box>
      </Box>
    );
  }

  if (isLoading || !config) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isClientReadOnly(user, config.client)) {
    return <ReadOnlyClientNotice clientName={config.client_name} onBack={goToList} />;
  }

  const steps = getPieceWorkflow(null).steps.map((step) =>
    step.key === "generation" ? { ...step, subtitle: "Escribe el texto de cada red" } : step
  );

  const labels = (list) => list.map((platform) => getPlatformMeta(platform).label).join(", ");
  const missingHint = isCreating
    ? ""
    : [
        missingText.length ? `Falta texto en: ${labels(missingText)}` : "",
        missingMedia.length ? `Falta la foto o el vídeo en: ${labels(missingMedia)}` : "",
      ]
        .filter(Boolean)
        .join(" · ");

  const createBar = (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 1.5 }}>
      {videoUpload && (
        <Typography role="status" aria-live="polite" sx={{ fontSize: "0.8125rem", color: "#4B5563" }}>
          {videoUpload.phase === "processing"
            ? `Procesando vídeo ${videoUpload.index} de ${videoUpload.total} en bundle.social…`
            : `Subiendo vídeo ${videoUpload.index} de ${videoUpload.total} · ${videoUpload.progress} %`}{" "}
          <Box component="span" sx={{ fontWeight: 700, color: "#374151" }}>
            No cierres esta página.
          </Box>
        </Typography>
      )}
      {/* Disabled buttons fire no events: the span carries the tooltip. */}
      <span>
          <Button variant="dark" onClick={() => handleCreate()} disabled={!canSaveDraft}>
            {creatingAction === "draft" && videoUpload ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <Save size={15} />}
            {creatingAction === "draft" && videoUpload ? "Subiendo vídeos..." : "Guardar borrador"}
          </Button>
      </span>
      <Tooltip title={missingHint} arrow>
        <span>
          <Button onClick={() => handleCreate({ send: true })} disabled={!canSend}>
            {creatingAction === "send" && videoUpload ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <Send size={15} />}
            {creatingAction === "send" && videoUpload ? "Subiendo vídeos..." : "Enviar al cliente"}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <WorkflowStepper steps={steps} selectedKey="generation" />

      <ConfirmModal
        open={Boolean(platformToRemove)}
        onClose={() => setPlatformToRemove(null)}
        onConfirm={() => {
          removePlatform(platformToRemove);
          setPlatformToRemove(null);
        }}
        title={`Quitar ${platformToRemove ? getPlatformMeta(platformToRemove).label : ""}`}
        description={
          <>
            Se perderán el texto, las imágenes y los vídeos que has añadido para{" "}
            <strong>{platformToRemove ? getPlatformMeta(platformToRemove).label : ""}</strong>. ¿Quieres quitar esta red?
          </>
        }
        confirmLabel="Quitar red"
        tone="warning"
      />

      {platforms.length === 0 ? (
        <Box
          sx={{
            ...panelSx,
            maxWidth: { md: 1440 }, // same as the stepper
            py: 8,
            px: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A" }}>
            Añade las redes sociales de este contenido
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280", maxWidth: 420 }}>
            Elige una red, escribe o pega su texto y añade sus imágenes o vídeos. Puedes pegar Markdown copiado de ChatGPT o Claude.
          </Typography>
          <AddNetworkButton usedPlatforms={platforms} onAdd={addPlatform} />
        </Box>
      ) : (
        // Same max width as the stepper.
        <Box sx={{ maxWidth: { md: 1440 } }}>
        <NetworkTabsPanel
          tabs={platforms.map((platform) => ({
            key: platform,
            platform,
            label: platformLabel(platform),
            meta: networkTabMeta(platform, formats[platform] || "post", texts[platform]),
            onRemove: isCreating ? null : () => requestRemove(platform),
          }))}
          activeKey={activePlatform}
          onSelect={setActivePlatform}
          addTab={<AddNetworkButton usedPlatforms={platforms} onAdd={addPlatform} />}
          trailing={createBar}
          // A network without phone preview (blog) gets no side column: the editor takes the whole width.
          preview={
            activePlatform && hasPostPreview(activePlatform) ? (
              <PostPhonePreview
                platform={activePlatform}
                text={texts[activePlatform]}
                images={images[activePlatform] || []}
                accountName={config.client_name}
                format={formats[activePlatform]}
                callToAction={callToActions[activePlatform]}
              />
            ) : null
          }
          previewCaption={
            activePlatform && hasPostPreview(activePlatform)
              ? `${platformLabel(activePlatform)} · ${formatLabel(activePlatform, formats[activePlatform] || "post")}`
              : ""
          }
        >
          {/* Every network stays mounted (hidden): its editor keeps its state between tabs. */}
          {platforms.map((platform) => (
            <Box key={platform} sx={{ display: platform === activePlatform ? "block" : "none" }}>
              <ManualNetworkCard
                embedded
                platform={platform}
                onUploadInlineImage={(file) => uploadInlineImage(configId, file)}
                text={texts[platform] || ""}
                images={images[platform] || []}
                onTextChange={(text) => setTexts((prev) => ({ ...prev, [platform]: text }))}
                onAddImages={(localImages) => addImages(platform, localImages)}
                onRemoveImage={(image) => removeImage(platform, image)}
                onCropImage={(image, cropped) => cropImage(platform, image, cropped)}
                format={formats[platform] || "post"}
                onChangeFormat={(format) => changeFormat(platform, format)}
                onAddVideo={(file) => addVideo(platform, file)}
                callToAction={callToActions[platform] || NO_CALL_TO_ACTION}
                onChangeCallToAction={(value) => setCallToActions((prev) => ({ ...prev, [platform]: value }))}
                onAddDocument={(file, title) => addDocument(platform, file, title)}
                onChangeDocumentTitle={(title) => changeDocumentTitle(platform, title)}
              />
            </Box>
          ))}
        </NetworkTabsPanel>
        </Box>
      )}
    </Box>
  );
}
